import { useState, type ReactNode } from "react";
import { Button, Card, Stack, Text, type ButtonVariant } from "@ferrivia/ui";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { Check } from "lucide-react";
import type {
  JourneyStepId,
  JourneyStepState,
  QuestionnaireSummaryListItem,
  ReadJourneyStep,
} from "@ferrivia/api-contract";
import { useAppContext } from "../../app/useAppContext";
import { IronPanelPurchaseSheet } from "../IronPanelPurchase";
import { UploadSheet } from "../LabUpload";
import { ScreenerSheet, useScreenerResumeLine } from "../Screener";
import s from "./JourneySteps.module.css";

/* The steps card both journey tabs share: labs, the screener, then booking a
 * time somewhere else (Sydänkeskus for infusion, Mari's calendar for the
 * alkukartoitus), each with where the client has got to. The tabs differ
 * only in copy, the analytics event and the booking action, which their
 * wrappers (InfusionSteps, SupportSteps) pass in.
 *
 * Which state each step is in is the server's (journey/steps.ts); what a step
 * and a state are called is the wrapper's, as every other block's copy is.
 * Nothing here reads a measured value, and no step's copy is chosen by one:
 * "done" on the labs step means a dated result exists inside the tab's
 * window, and the card says exactly that and nothing more.
 *
 * One primary control across the whole card, not one per step: the platform's
 * "yks action kerrallaan" still holds inside a card that shows three steps —
 * the client's next actual move gets the weight and everything else stays
 * secondary. `muted` hands that weight to something else — the support tab's
 * offer, passed in as `lead` and drawn between the title and the steps.
 *
 * Outside Finland there is nothing to buy: the labs step offers only upload,
 * which then carries the step's weight, plus whatever `labsExtra` the wrapper
 * passes (the US support tab's pointer to where labs can be ordered).
 */

export type JourneyStepCta = "labs_buy" | "labs_upload" | "screener";

type StepCopy = { label: string; body: Partial<Record<JourneyStepState, string>> };

export type JourneyStepsCopy = {
  eyebrow: string;
  title: string;
  steps: Record<JourneyStepId, StepCopy> & {
    labs: { storeCta: string; uploadCta: string };
  };
};

// The step a client can act on right now, in order. Labs in progress means a
// panel is bought and the lab has it: the client is waiting rather than
// acting, so the weight moves on to the screener (they can still add results
// of their own from the quiet control the labs step keeps).
function isActionable(step: ReadJourneyStep): boolean {
  if (step.id === "screener") return step.state === "todo" || step.state === "in_progress";
  return step.state === "todo";
}

export function JourneySteps({
  steps,
  summaries = [],
  refetch,
  copy,
  track,
  renderBooking,
  summaryPath,
  muted = false,
  lead,
  labsExtra,
}: {
  steps: ReadJourneyStep[];
  summaries?: readonly QuestionnaireSummaryListItem[];
  refetch: () => void;
  copy: JourneyStepsCopy;
  track: (cta: JourneyStepCta) => void;
  // Only called while booking is `todo`; the wrapper tracks its own click.
  renderBooking: (variant: ButtonVariant) => ReactNode;
  summaryPath: "infusion" | "support";
  muted?: boolean;
  lead?: ReactNode;
  // Secondary, after upload, only while the step is todo.
  labsExtra?: ReactNode;
}) {
  const { t } = useTranslation("client");
  const { openSummary } = useAppContext();
  const resumeLineFor = useScreenerResumeLine();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const commerce = useMarket() === "fi";

  /* A word, not a colour or a tick alone: the state has to survive being read
     out and being looked at in greyscale. `todo` gets none — an unmarked step
     is one nobody has touched, and labelling that adds nothing. */
  const stateLabels: Partial<Record<string, string>> = {
    done: t("infusion.steps.state.done", "Valmis"),
    in_progress: t("infusion.steps.state.inProgress", "Kesken"),
    locked: t("infusion.steps.state.locked", "Avautuu myöhemmin"),
  };

  // A step id this build has never heard of has no name and no sentence, so
  // it is left out rather than shown as a blank row — the lenient-read rule
  // readHomeBlockSchema sets for block types, applied one level down
  // (readJourneyStepSchema). Filtered before numbering, so the numerals stay
  // 1, 2, 3 for the steps actually shown.
  const knownSteps = steps.flatMap((step) => {
    const stepCopy = (copy.steps as Partial<Record<string, StepCopy>>)[step.id];
    return stepCopy === undefined ? [] : [{ step, stepCopy }];
  });

  const leadStepId = muted ? undefined : steps.find(isActionable)?.id;
  const weight = (stepId: JourneyStepId): ButtonVariant => (stepId === leadStepId ? "primary" : "secondary");

  const handleBuyClick = () => {
    track("labs_buy");
    setPurchaseOpen(true);
  };

  const handleUploadClick = () => {
    track("labs_upload");
    setUploadOpen(true);
  };

  const actionsFor = (step: ReadJourneyStep) => {
    if (step.id === "labs") {
      if (step.state === "done") return null;
      return (
        <div className={s.actions}>
          {commerce && step.state === "todo" && (
            <Button variant={weight("labs")} onClick={handleBuyClick}>
              {copy.steps.labs.storeCta}
            </Button>
          )}
          {/* Stays available while a bought panel is in flight: a client who
              already has results of their own inside the window can satisfy
              this step with them instead of waiting for ours. */}
          <Button variant={commerce ? "secondary" : weight("labs")} onClick={handleUploadClick}>
            {copy.steps.labs.uploadCta}
          </Button>
          {step.state === "todo" && labsExtra}
        </div>
      );
    }

    if (step.id === "screener") {
      if (step.state === "done") {
        if (summaries.length === 0) return null;
        return (
          <div className={s.actions}>
            <button
              type="button"
              className={s.summaryLink}
              onClick={() => openSummary(summaries[0].id, { source: `${summaryPath}_step`, path: summaryPath })}
            >
              {t("summary.viewLink", "Katso raportti")}
            </button>
          </div>
        );
      }
      return (
        <div className={s.actions}>
          <ScreenerSheet
            onOpen={() => track("screener")}
            resumable={step.state === "in_progress"}
            variant={weight("screener")}
          />
        </div>
      );
    }

    // Booking has no `done` — the time is booked in someone else's calendar
    // and never becomes a record here (journey/steps.ts). Whitelisted rather
    // than excluding "locked": a state this build has never heard of shows
    // the step without an action, as the copy lookup below does with its body.
    if (step.id !== "booking" || step.state !== "todo") return null;
    return <div className={s.actions}>{renderBooking(weight("booking"))}</div>;
  };

  return (
    <>
      <Card>
        <Stack gap="4">
          <div>
            <Text as="p" size="xs" weight="medium" className={s.eyebrow}>
              {copy.eyebrow}
            </Text>
            <Text as="h2" size="lg" weight="bold" className={s.title}>
              {copy.title}
            </Text>
          </div>

          {lead}

          <ol className={s.steps}>
            {knownSteps.map(({ step, stepCopy }, index) => {

              // Replaces the generic in-progress sentence with where the
              // client actually got to, once there's something to say —
              // falls back to it when the submission is open but nothing has
              // been saved yet (no lastSavedAt to report).
              const resumeLine = step.id === "screener" ? resumeLineFor(step) : undefined;
              const body = resumeLine ?? (stepCopy.body as Partial<Record<string, string>>)[step.state];

              return (
                <li key={step.id} className={s.step} data-state={step.state}>
                  {/* aria-hidden: the <ol> already numbers these for a
                      screen reader, and the numeral is the label's visual
                      companion. */}
                  <span className={s.numeral} aria-hidden="true">
                    {index + 1}
                  </span>
                  <div>
                    <Text as="p" size="xs" weight="medium" className={s.label}>
                      {stepCopy.label}
                      {stateLabels[step.state] !== undefined && (
                        <span className={s.state} data-state={step.state}>
                          {step.state === "done" && <Check aria-hidden size={12} strokeWidth={3} />}
                          {stateLabels[step.state]}
                        </span>
                      )}
                    </Text>
                    {/* Likewise a state this build does not know: the step
                        keeps its name and loses its sentence. */}
                    {body !== undefined && (
                      <Text as="p" size="md" className={s.body}>
                        {body}
                      </Text>
                    )}
                    {actionsFor(step)}
                  </div>
                </li>
              );
            })}
          </ol>
        </Stack>
      </Card>

      {/* The Results tab's self-report form and the iron panel purchase —
          existing surfaces mounted here, not reimplemented. */}
      <UploadSheet open={uploadOpen} onOpenChange={setUploadOpen} onSaved={refetch} />
      {commerce && <IronPanelPurchaseSheet open={purchaseOpen} onOpenChange={setPurchaseOpen} />}
    </>
  );
}
